import React, { useState } from "react";
import { useTheme } from "src/context/ThemeContext";
import { TablePagination } from "src/shared/components/table";
import type {
  MerchantProductPagination,
  StockUpdateRequest,
  PriceUpdateRequest,
} from "../../../../types";
import type { UIMerchantProduct } from "../../../../utils/dataTransformers";
import MerchantProductVariantRow, {
  type VariantData,
} from "./MerchantProductVariantRow";

interface MerchantProductTableProps {
  products: UIMerchantProduct[];
  pagination: MerchantProductPagination;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onStockUpdate: (request: StockUpdateRequest) => void;
  onPriceUpdate: (request: PriceUpdateRequest) => void;
  onRowClick?: (product: UIMerchantProduct) => void;
  onEditProduct?: (product: UIMerchantProduct) => void;
  showVariants?: boolean;
  'data-testid'?: string;
}

const MerchantProductTable: React.FC<MerchantProductTableProps> = ({
  products,
  pagination,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onStockUpdate,
  onPriceUpdate,
  onRowClick,
  onEditProduct,
  showVariants = false,
  'data-testid': dataTestId,
}) => {
  const { theme } = useTheme();
  const [editingStock, setEditingStock] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [stockValue, setStockValue] = useState<number>(0);
  const [priceValue, setPriceValue] = useState<number>(0);
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(
    new Set()
  );

  // Handle variant toggle
  const toggleVariantExpansion = (productId: string) => {
    const newExpanded = new Set(expandedVariants);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
    } else {
      newExpanded.add(productId);
    }
    setExpandedVariants(newExpanded);
  };

  // Handle variant row click
  const handleVariantClick = (variant: VariantData) => {
    console.log("Variant clicked:", variant.sku, variant.id);
    // Navigate to variant detail or trigger action
  };

  const handleStockEdit = (product: UIMerchantProduct) => {
    setEditingStock(product.id);
    setStockValue(product.stock.quantity);
  };

  const handleStockSave = (productId: string) => {
    onStockUpdate({ productId, newQuantity: stockValue });
    setEditingStock(null);
  };

  const handlePriceEdit = (product: UIMerchantProduct) => {
    setEditingPrice(product.id);
    setPriceValue(product.price.current);
  };

  const handlePriceSave = (productId: string) => {
    onPriceUpdate({ productId, newPrice: priceValue });
    setEditingPrice(null);
  };

  const formatCurrency = (amount: number, currency: string = "IDR") => {
    if (currency === "IDR") {
      return `Rp ${amount.toLocaleString("id-ID")}`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const getStockStatusColor = (status: string) => {
    switch (status) {
      case "warning":
        return "tw-text-orange-600";
      case "low-stock":
        return "tw-text-yellow-600";
      case "out-of-stock":
        return "tw-text-red-600";
      default:
        return theme === "light" ? "tw-text-gray-800" : "tw-text-slate-200";
    }
  };

  const renderStockCell = (product: UIMerchantProduct) => {
    const isEditing = editingStock === product.id;
    const stockColor = getStockStatusColor(product.stock.status);

    if (isEditing) {
      return (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <input
            type="number"
            value={stockValue}
            onChange={(e) => setStockValue(Number(e.target.value))}
            className="tw-w-16 tw-px-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            onBlur={() => handleStockSave(product.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleStockSave(product.id);
              if (e.key === "Escape") setEditingStock(null);
            }}
            autoFocus
          />
        </div>
      );
    }

    return (
      <div className="tw-flex tw-items-center tw-gap-1.5">
        {product.stock.status === "warning" && (
          <span className="material-symbols-outlined tw-text-base tw-text-orange-600">
            warning
          </span>
        )}
        <span className={`tw-font-medium ${stockColor}`}>
          {product.stock.quantity}
        </span>
        {product.stock.isEditable && (
          <button
            onClick={() => handleStockEdit(product)}
            className="tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors"
          >
            <span className="material-symbols-outlined tw-text-base">edit</span>
          </button>
        )}
      </div>
    );
  };

  const renderPriceCell = (product: UIMerchantProduct) => {
    const isEditing = editingPrice === product.id;

    if (isEditing) {
      return (
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-relative">
            <span className="tw-absolute tw-left-2 tw-top-1 tw-text-xs tw-text-gray-500">
              Rp
            </span>
            <input
              type="number"
              value={priceValue}
              onChange={(e) => setPriceValue(Number(e.target.value))}
              className="tw-w-24 tw-pl-8 tw-pr-2 tw-py-1 tw-text-sm tw-border tw-border-gray-300 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
              onBlur={() => handlePriceSave(product.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePriceSave(product.id);
                if (e.key === "Escape") setEditingPrice(null);
              }}
              autoFocus
            />
          </div>
        </div>
      );
    }

    if (product.variants && product.variants.priceRange) {
      return (
        <span
          className={`tw-font-medium ${
            theme === "light" ? "tw-text-gray-800" : "tw-text-slate-200"
          }`}
        >
          {formatCurrency(product.variants.priceRange.min)} -{" "}
          {formatCurrency(product.variants.priceRange.max)}
        </span>
      );
    }

    return (
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <span
          className={`tw-font-medium ${
            theme === "light" ? "tw-text-gray-800" : "tw-text-slate-200"
          }`}
        >
          {formatCurrency(product.price.current)}
        </span>
        {product.price.isEditable && (
          <button
            onClick={() => handlePriceEdit(product)}
            className="tw-text-gray-400 hover:tw-text-blue-600 tw-transition-colors"
          >
            <span className="material-symbols-outlined tw-text-base">edit</span>
          </button>
        )}
      </div>
    );
  };

  const renderSkeletonRow = () => (
    <tr key={`skeleton-${Math.random()}`}>
      {/* Product column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-flex tw-items-start tw-gap-3">
          <div className="tw-h-10 tw-w-10 tw-rounded tw-bg-gray-200 dark:tw-bg-slate-700 tw-animate-pulse"></div>
          <div className="tw-flex-1 tw-space-y-2">
            <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-3/4"></div>
            <div className="tw-h-3 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-1/2"></div>
          </div>
        </div>
      </td>
      {/* SKU column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-20"></div>
      </td>
      {/* Price column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-24"></div>
          <div className="tw-h-4 tw-w-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
        </div>
      </td>
      {/* Stock column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-12"></div>
          <div className="tw-h-4 tw-w-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse"></div>
        </div>
      </td>
      {/* Sales (30d) column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-8"></div>
      </td>
      {/* Revenue (30d) column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-4 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded tw-animate-pulse tw-w-20"></div>
      </td>
      {/* Actions column */}
      <td className="tw-px-4 tw-py-3 tw-align-top">
        <div className="tw-h-8 tw-w-8 tw-bg-gray-200 dark:tw-bg-slate-700 tw-rounded-lg tw-animate-pulse"></div>
      </td>
    </tr>
  );

  const renderEmptyState = () => (
    <tr>
      <td colSpan={7} className="tw-px-6 tw-py-16 tw-text-center">
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <div
            className={`tw-mb-6 tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full ${
              theme === "light" ? "tw-bg-gray-50" : "tw-bg-slate-700"
            }`}
          >
            <span className="material-symbols-outlined tw-text-5xl tw-text-gray-400">
              search_off
            </span>
          </div>
          <h3
            className={`tw-text-xl tw-font-semibold tw-mb-2 ${
              theme === "light" ? "tw-text-gray-900" : "tw-text-slate-100"
            }`}
          >
            No Product Merchant Available
          </h3>
          <p
            className={`tw-text-sm tw-mb-6 tw-max-w-sm ${
              theme === "light" ? "tw-text-gray-500" : "tw-text-slate-400"
            }`}
          >
            Your search criteria didn't match any products. Try clearing filters
            or adding a new product.
          </p>
          <button
            onClick={() => {
              console.log("Add Product clicked");
            }}
            className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-primary/90 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2"
          >
            <span className="material-symbols-outlined tw-mr-2 tw-text-xl">
              add_circle
            </span>
            Add Product Merchant
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="tw-overflow-x-auto" data-testid={dataTestId ? `${dataTestId}-container` : "merchant-product-table-container"}>
      <div className="tw-inline-block tw-min-w-full tw-align-middle">
        <div
          className={`tw-overflow-hidden tw-rounded-lg tw-border ${
            theme === "light"
              ? "tw-border-gray-200 tw-bg-white"
              : "tw-border-slate-600 tw-bg-slate-800"
          }`}
        >
          <table className="tw-min-w-full tw-divide-y tw-divide-gray-200 dark:tw-divide-slate-600" data-testid={dataTestId || "merchant-product-table"}>
            <thead
              className={
                theme === "light" ? "tw-bg-gray-50" : "tw-bg-slate-700"
              }
              data-testid="product-table-header"
            >
              <tr>
                <th
                  scope="col"
                  className="tw-w-[35%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Product
                </th>
                <th
                  scope="col"
                  className="tw-w-[15%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  SKU
                </th>
                <th
                  scope="col"
                  className="tw-w-[15%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Price
                </th>
                <th
                  scope="col"
                  className="tw-w-[10%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Stock
                </th>
                <th
                  scope="col"
                  className="tw-w-[10%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Sales (30d)
                </th>
                <th
                  scope="col"
                  className="tw-w-[15%] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Revenue (30d)
                </th>
                <th
                  scope="col"
                  className="tw-w-[100px] tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-tracking-wider tw-text-gray-500 dark:tw-text-slate-300"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={`tw-divide-y ${
                theme === "light" ? "tw-divide-gray-200" : "tw-divide-slate-600"
              }`}
              data-testid="product-table-body"
            >
              {loading
                ? // Loading skeleton rows
                  Array.from({ length: 5 }, (_, index) => renderSkeletonRow())
                : products.length === 0
                ? renderEmptyState()
                : products.map((product) => (
                    <React.Fragment key={product.id}>
                      {/* Main Product Row */}
                      <tr
                        data-testid={`product-row-${product.id}`}
                        onClick={() => onRowClick?.(product)}
                        className={`${onRowClick ? "tw-cursor-pointer" : ""} ${
                          theme === "light"
                            ? "hover:tw-bg-gray-50"
                            : "hover:tw-bg-slate-700"
                        } tw-transition-colors`}
                      >
                        <td className="tw-px-4 tw-py-3 tw-align-top" data-testid="product-cell">
                          <div className="tw-flex tw-items-start tw-gap-3">
                            {product.image && (
                              <img
                                className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded tw-object-cover"
                                src={product.image}
                                alt={product.name}
                              />
                            )}
                            <div className="tw-flex tw-flex-col tw-gap-0.5">
                              <p
                                data-testid="product-name"
                                className={`tw-text-sm tw-font-medium ${
                                  theme === "light"
                                    ? "tw-text-gray-800"
                                    : "tw-text-slate-200"
                                }`}
                              >
                                {product.name}
                              </p>
                              <p className="tw-text-xs tw-text-gray-500 dark:tw-text-slate-400">
                                {product.brand} • {product.category}
                              </p>
                              {product.variants && showVariants && (
                                <div className="tw-mt-1">
                                  <button
                                    data-testid="variant-toggle"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleVariantExpansion(product.id);
                                    }}
                                    className="tw-text-sm tw-font-medium tw-text-primary hover:tw-text-primary/80"
                                  >
                                    {expandedVariants.has(product.id)
                                      ? "Hide Variants"
                                      : `View ${
                                          product.variants.count
                                        } Variant${
                                          product.variants.count === 1
                                            ? ""
                                            : "s"
                                        }`}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td
                          data-testid="product-sku"
                          className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium ${
                            theme === "light"
                              ? "tw-text-gray-800"
                              : "tw-text-slate-200"
                          }`}
                        >
                          {product.sku}
                        </td>
                        <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm">
                          {renderPriceCell(product)}
                        </td>
                        <td className="tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm">
                          {renderStockCell(product)}
                        </td>
                        <td
                          className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium ${
                            theme === "light"
                              ? "tw-text-gray-800"
                              : "tw-text-slate-200"
                          }`}
                        >
                          {product.sales.quantity30d}
                        </td>
                        <td
                          className={`tw-whitespace-nowrap tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium ${
                            theme === "light"
                              ? "tw-text-gray-800"
                              : "tw-text-slate-200"
                          }`}
                        >
                          {formatCurrency(product.sales.revenue30d)}
                        </td>
                        <td className="tw-px-4 tw-py-3 tw-align-top tw-text-sm tw-font-medium">
                          <div className="tw-relative tw-inline-block">
                            <button
                              className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg ${
                                theme === "light"
                                  ? "tw-text-gray-600 hover:tw-bg-gray-100 hover:tw-text-gray-900"
                                  : "tw-text-slate-400 hover:tw-bg-slate-600 hover:tw-text-slate-200"
                              } tw-transition-colors`}
                              title="Edit Product"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditProduct?.(product);
                              }}
                            >
                              <span className="material-symbols-outlined tw-text-lg">
                                edit
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Variant Rows */}
                      {product.variants &&
                        showVariants &&
                        expandedVariants.has(product.id) &&
                        product.variants.combinations?.map((variant) => (
                          <MerchantProductVariantRow
                            key={variant.id}
                            variant={variant}
                            parentProductId={product.id}
                            onRowClick={handleVariantClick}
                          />
                        ))}
                    </React.Fragment>
                  ))}
            </tbody>
          </table>

          {/* Shared Pagination Component */}
          <TablePagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            itemName="product merchant"
            loading={loading}
            data-testid="merchant-product-pagination"
          />
        </div>
      </div>
    </div>
  );
};

export default MerchantProductTable;
